import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useOrg } from "@/pages/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, Sparkles, ExternalLink, ShieldCheck } from "lucide-react";

export default function Subscription() {
  const { org, reloadOrg } = useOrg();
  const [config, setConfig] = useState({ mp_checkout_url: null, mp_configured: false });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [paymentId, setPaymentId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/subscription/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  if (!org) return null;

  const trialEnd = new Date(org.trial_end);
  const daysLeft = Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000));

  const goToCheckout = () => {
    if (config.mp_checkout_url) {
      window.open(config.mp_checkout_url, "_blank", "noopener,noreferrer");
      setTimeout(() => setConfirmOpen(true), 2000);
    }
  };

  const submitConfirm = async () => {
    const id = paymentId.trim();
    if (!id) return toast.error("Pegá el código de pago");
    setSubmitting(true);
    try {
      const { data } = await api.post("/subscription/confirm", { payment_id: id });
      toast.success(data.message || "Plan activado");
      setConfirmOpen(false);
      setPaymentId("");
      reloadOrg();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo confirmar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-2">// Tu plan</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">SUSCRIPCIÓN</h1>
      </div>

      <div className="max-w-3xl grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-[#141414] border border-[#262626] rounded-lg p-8" data-testid="current-plan-card">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-3">Estado actual</div>
          {org.plan_status === "trial" && (
            <>
              <div className="font-heading text-4xl mb-2 text-amber-400">PRUEBA</div>
              <div className="text-zinc-400 mb-6">{daysLeft} {daysLeft === 1 ? "día restante" : "días restantes"}</div>
              <div className="text-sm text-zinc-500">Disfrutá de todas las funciones mientras evaluás la plataforma.</div>
            </>
          )}
          {org.plan_status === "expired" && (
            <div data-testid="expired-plan-state">
              <div className="font-heading text-4xl mb-2 text-red-400">PRUEBA VENCIDA</div>
              <div className="text-zinc-400 mb-6">Tus 10 días gratis terminaron</div>
              <div className="text-sm text-zinc-500">Tu dashboard y tu página de reservas están pausados. Activá el Plan Club y todo vuelve a funcionar al instante, sin perder tus datos.</div>
            </div>
          )}
          {org.plan_status === "active" && (
            <>
              <div className="font-heading text-4xl mb-2 text-emerald-400">PLAN CLUB</div>
              <div className="text-zinc-400 mb-6">Activo</div>
              <div className="text-sm text-zinc-500">Gracias por confiar en MESSIRVE 🙌</div>
              {org.last_payment_id && (
                <div className="text-xs text-zinc-600 mt-3">Último pago: <span className="text-zinc-400 font-mono">{org.last_payment_id}</span></div>
              )}
            </>
          )}
        </div>

        <div className="bg-[#141414] border-2 border-emerald-500 rounded-lg p-8 relative glow-emerald">
          <div className="absolute -top-3 left-8 bg-emerald-500 text-black text-xs uppercase tracking-[0.2em] font-bold px-3 py-1 rounded flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Upgrade
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-3">Plan Club</div>
          <div className="font-heading text-5xl mb-2">$30.000</div>
          <div className="text-zinc-400 text-sm mb-6">ARS por mes</div>
          <ul className="space-y-2 mb-8 text-sm">
            {["Turnos ilimitados", "Canchas ilimitadas", "Confirmación WhatsApp", "Marca personalizada", "Soporte prioritario"].map((x) => (
              <li key={x} className="flex items-center gap-2 text-zinc-300"><Check className="h-4 w-4 text-emerald-400" /> {x}</li>
            ))}
          </ul>
          <Button
            onClick={goToCheckout}
            disabled={org.plan_status === "active" || !config.mp_checkout_url}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-black uppercase tracking-widest font-bold disabled:opacity-50"
            data-testid="upgrade-btn"
          >
            {org.plan_status === "active" ? "Plan activo" : (
              <>Suscribirme por MercadoPago <ExternalLink className="h-3 w-3 ml-2" /></>
            )}
          </Button>
          {org.plan_status !== "active" && (
            <button
              onClick={() => setConfirmOpen(true)}
              className="w-full mt-3 text-xs text-zinc-500 hover:text-emerald-400 uppercase tracking-widest underline-offset-4 hover:underline"
              data-testid="already-paid-btn"
            >
              Ya pagué, confirmar código
            </button>
          )}
          <p className="text-xs text-zinc-500 mt-4 flex items-start gap-2">
            {config.mp_configured ? (
              <>
                <ShieldCheck className="h-3 w-3 mt-0.5 text-emerald-400 shrink-0" />
                <span>Verificamos el pago con MercadoPago para activarte en el momento.</span>
              </>
            ) : (
              <span>MercadoPago no está configurado — contactá al soporte.</span>
            )}
          </p>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#141414] border-[#262626]">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-wider">CONFIRMAR PAGO</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">
              Pegá el <b className="text-white">ID de transacción</b> o <b className="text-white">código de pago</b> que MercadoPago te devolvió al terminar la compra (también lo recibís por email).
            </p>
            <div>
              <Label className="uppercase tracking-widest text-xs text-zinc-400">Código de pago</Label>
              <Input
                value={paymentId}
                onChange={(e) => setPaymentId(e.target.value)}
                placeholder="Ej. 73825161234 ó preapproval-id"
                className="bg-[#0A0A0A] border-[#262626] mt-2 h-12 font-mono"
                data-testid="confirm-payment-input"
              />
              <p className="text-xs text-zinc-500 mt-2">
                Lo encontrás en MercadoPago → Actividad → Último movimiento → "Nº de operación".
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmOpen(false)}
                className="border-zinc-700 bg-transparent text-zinc-400 hover:bg-[#141414] uppercase tracking-widest h-12 flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={submitConfirm}
                disabled={submitting}
                className="bg-emerald-500 hover:bg-emerald-600 text-black uppercase tracking-widest font-bold h-12 flex-1"
                data-testid="confirm-payment-submit"
              >
                {submitting ? "Verificando..." : "Activar plan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
